import { GoogleAuthError, LoginError, ReduxState, themes, utils } from 'kb-shared';
import Honeybadger from '@honeybadger-io/js';
import React, { ChangeEvent, SyntheticEvent } from 'react';
import { connect } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router';
import styled from 'styled-components';

import * as redux from '../../redux';
import Button from '../../components/Button/NewButton';
import GoogleAuthButton from '../../components/GoogleAuthButton';
import AppleAuthButton from 'components/AppleAuthButton';
import TextInput from '../../components/TextInput/NewTextInput';
import { showErrorToast } from '../../utilities/notificationUtils';
import SignUpBox from './components/SignUpBox';
import ActivateBenefitBox from './components/ActivateBenefitBox';
import { AppleAuthError } from 'kb-shared/types';

const { isEmailValid, isPasswordValid } = utils;
const KBContacts = utils.KBContacts;
const { login } = redux.patient;

const LoginContainer = styled.div`
  display: flex;
  align-items: center;
  flex-direction: column;
  width: 100%;
`;

const LoginContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-bottom: 48px;
  padding-top: 20px;
`;

const AuthButtonWrapper = styled.div`
  padding-bottom: 24px;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const ButtonWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding-top: 40px;
`;

const ForgotPasswordLink = styled.a`
  ${themes.fonts.FoundersGrotesk};
  font-size: 14px;
  font-weight: bold;
  text-align: center;
  padding-top: 28px;
  padding-bottom: 28px;
  text-decoration: underline;
  color: ${themes.colors.KBNight};
`;

const VTextFieldMargin = styled.div`
  margin: 15px 0;
`;

interface Props extends RouteComponentProps {
  mobile: boolean;
  showSignUpBoxMobile: boolean;
  onLogin: () => void;
  onGoogleAuthError?: (error: GoogleAuthError, googleUserId: string | null) => void;
  onError: (error: LoginError, email: string) => void;
  login: (username: string, password: string) => void;
  onForgotPassword: () => void;
  onSignUpPress?: () => void;
  isLoggedIn: boolean;
  authCheckInProgress: boolean;
  loginError?: LoginError | null;
  loading: boolean;
  googleAuthError?: GoogleAuthError | null;
  appleAuthError?: AppleAuthError | null;
  googleUserId?: number | null;
}

type State = {
  emailAddress: string;
  password: string;
  emailError: boolean;
  passwordError: boolean;
  maskedPassword: boolean;
};

class LoginFlow extends React.Component<Props, State> {
  state = {
    emailAddress: '',
    emailError: false,
    password: '',
    passwordError: false,
    maskedPassword: true
  };

  componentDidMount() {
    if (window.analytics) {
      window.analytics.page('Log In Page');
    }
  }

  UNSAFE_componentWillMount() {
    const { isLoggedIn, googleAuthError, googleUserId } = this.props;
    if (isLoggedIn) {
      this.props.onLogin();
    }
    if (googleAuthError) {
      // @ts-ignore
      this.props.onGoogleAuthError(googleAuthError, googleUserId);
    }
  }

  UNSAFE_componentWillReceiveProps(newProps: Props) {
    if (newProps.isLoggedIn) {
      this.props.onLogin();
    }

    if (newProps.googleAuthError && this.props.googleAuthError !== newProps.googleAuthError) {
      // @ts-ignore
      this.props.onGoogleAuthError(newProps.googleAuthError, newProps.googleUserId);
    }

    if (!newProps.loginError) return;
    const loginError: LoginError = newProps.loginError;

    const alreadyHandled =
      this.props.loginError && newProps.loginError.type !== this.props.loginError.type;

    if (!alreadyHandled) {
      switch (newProps.loginError.type) {
        case 'Unconfirmed':
          showErrorToast(
            'You have an account that has not been confirmed, please check your email for a verification code.'
          );
          this.props.onError(loginError, this.state.emailAddress);
          break;
        case 'InvalidCredentials':
          showErrorToast(
            'We were unable to proceed with this request. If you are registered with a Gmail account, please log in with Google. If you are registered with an Apple ID, please log in with Apple. Otherwise, please check your credentials and try again.'
          );
          break;
        case 'LimitExceededException':
          showErrorToast(
            `You have exceeded the limit for making a login request. Please wait then try again, or contact ${KBContacts.navigatorEmail}`
          );
          break;
        default:
          showErrorToast('Something went wrong. Please try again.');
          Honeybadger.notify(loginError.type, 'LoginError');
          break;
      }
    }
  }

  componentDidUpdate(prevProps: Props) {
    if (this.props.appleAuthError && prevProps.appleAuthError !== this.props.appleAuthError) {
      this.handleAppleAuthError(this.props.appleAuthError);
    }
  }

  handleAppleAuthError = (error: AppleAuthError) => {
    if (error.type == 'UserHasNotSignedUp') {
      showErrorToast(
        'We were unable to proceed with this request. If you are registered with a Gmail account, please log in with Google. If you are registered with an Apple ID, please log in with Apple. Otherwise, please check your credentials and try again.'
      );
    }
  };

  updateEmail = (event: ChangeEvent<HTMLInputElement>) => {
    this.setState({
      emailAddress: event.currentTarget.value,
      emailError: false
    });
  };

  updatePassword = (event: ChangeEvent<HTMLInputElement>) => {
    this.setState({
      password: event.currentTarget.value,
      passwordError: false
    });
  };

  toggleMaskedPassword = () => this.setState({ maskedPassword: !this.state.maskedPassword });

  handleSubmit = async (event: SyntheticEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!this.validate()) {
      showErrorToast(
        'We were unable to proceed with this request. If you are registered with a Gmail account, please log in with Google. If you are registered with an Apple ID, please log in with Apple. Otherwise, please check your credentials and try again.'
      );
      return;
    }

    const { emailAddress, password } = this.state;
    this.props.login(emailAddress, password);
  };

  validate = () => {
    const { emailAddress, password } = this.state;
    const emailValid = isEmailValid(emailAddress);
    const passwordValid = isPasswordValid(password);
    this.setState({
      emailError: !emailValid,
      passwordError: !passwordValid
    });
    return emailValid && passwordValid;
  };

  render() {
    const { emailAddress, emailError, password, passwordError, maskedPassword } = this.state;
    const { loading, showSignUpBoxMobile, authCheckInProgress, isLoggedIn } = this.props;
    return (
      <LoginContainer>
        <LoginContent>
          <AuthButtonWrapper>
            <GoogleAuthButton type={'light'} />
          </AuthButtonWrapper>

          <AuthButtonWrapper>
            <AppleAuthButton operation="login" />
          </AuthButtonWrapper>

          <Form onSubmit={this.handleSubmit} autoComplete="new-password">
            <VTextFieldMargin>
              <TextInput
                name={'email'}
                label={'EMAIL ADDRESS'}
                value={emailAddress}
                onChange={this.updateEmail}
                error={emailError}
                autoComplete="email"
                spellCheck="false"
                placeholder={'janedoe@email.com'}
                inputMode="email"
              />
            </VTextFieldMargin>

            <VTextFieldMargin>
              <TextInput
                name={'password'}
                label={'PASSWORD'}
                value={password}
                onChange={this.updatePassword}
                type={maskedPassword ? 'password' : 'text'}
                error={passwordError}
                autoComplete="password"
              />
            </VTextFieldMargin>

            <ButtonWrapper>
              <Button
                loading={loading || authCheckInProgress || isLoggedIn}
                disabled={loading || authCheckInProgress || isLoggedIn}
                type="submit"
                text="LOG IN"
              />
            </ButtonWrapper>

            <ForgotPasswordLink onClick={this.props.onForgotPassword}>
              Forgot your password?
            </ForgotPasswordLink>

            {showSignUpBoxMobile ? (
              <>
                <SignUpBox
                  mobile={true}
                  onSignUpPress={() => {
                    this.props.onSignUpPress && this.props.onSignUpPress();
                  }}
                />
                <ActivateBenefitBox
                  mobile={true}
                  onActivateBenefitPress={() => {
                    this.props.history.replace('/kindbody-benefits');
                  }}
                />
              </>
            ) : null}
          </Form>
        </LoginContent>
      </LoginContainer>
    );
  }
}

const mapStateToProps = ({
  patient: {
    isLoggedIn,
    loading,
    loginError,
    authCheckInProgress,
    googleAuthError,
    appleAuthError,
    patient: patient
  }
}: ReduxState) => ({
  isLoggedIn: isLoggedIn,
  loading: loading,
  loginError: loginError,
  authCheckInProgress: authCheckInProgress,
  googleAuthError: googleAuthError,
  appleAuthError: appleAuthError,
  googleUserId: patient.google ? patient.google.id : null
});

const mapDispatchToProps = (dispatch: Function) => ({
  login: (username: string, password: string) => dispatch(login(username, password))
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(LoginFlow));
